import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `YYYY-MM-DD` that is also a real calendar date.
 *
 * The regex alone accepts `2026-02-30` and `2026-13-01`; Postgres would reject
 * those at the wire, producing a 500 instead of a 400. Round-tripping through a
 * UTC-noon `Date` and comparing the rendered string back is the cheap, DST-safe
 * check — the same one `handlers/timesheets/week-utils.ts` uses.
 */
const isoCalendarDate = z
  .string()
  .regex(DATE_REGEX, 'must be an ISO date (YYYY-MM-DD)')
  .refine((v) => {
    const d = new Date(`${v}T12:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
  }, 'is not a valid calendar date');

export const AdminUserDto = z.object({
  userId: z.string().uuid(),
  utEid: z.string().nullable(),
  email: z.string(),
  displayName: z.string(),
  // Avatar initials, computed by the API from the AD name parts
  // (api/src/repositories/person.ts). The web renders this verbatim and
  // must never derive initials itself.
  initials: z.string(),
  role: z.enum(['contributor', 'approver', 'administrator']),
  activeFlag: z.boolean(),
  weeklyMinuteTarget: z.number().int().positive().nullable(),
  // The administrator override for the date this person landed in the system.
  // null means no override — the system falls back to the Monday of the
  // America/Chicago week containing `createdAt`.
  systemStartDate: isoCalendarDate.nullable(),
  // What the override resolves to, with the null case already coalesced. Always
  // populated. Returned alongside the raw override so the admin UI can show the
  // fallback date without re-implementing the server's Chicago-week coalesce.
  effectiveSystemStartDate: isoCalendarDate,
  // Set while AD sync has this person on course for deactivation: missing from
  // the synced group since this instant, but still inside the grace period. null
  // when they are present in the group, or not active.
  pendingDeactivationSince: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminUserDto = z.infer<typeof AdminUserDto>;

export const AdminUsersResponse = z.array(AdminUserDto);
export type AdminUsersResponse = z.infer<typeof AdminUsersResponse>;

export const UpdateUserRoleRequest = z.object({
  // Only 'administrator' (grant) and 'contributor' (revoke — restores role
  // derived from team memberships) are supported. 'approver' is never a
  // directly settable value; it is computed from team membership.
  role: z.enum(['contributor', 'administrator']),
});
export type UpdateUserRoleRequest = z.infer<typeof UpdateUserRoleRequest>;

export const UpdateUserActiveRequest = z.object({
  // true reactivates the user (and clears any admin deactivation); false is an
  // explicit admin deactivation that survives AD sync (decision D2).
  active: z.boolean(),
});
export type UpdateUserActiveRequest = z.infer<typeof UpdateUserActiveRequest>;

export const PatchAdminUserHourTargetRequest = z.object({
  // Minutes per week. null resets to the system default (2400 = 40h).
  // When set, must be between 60 (1h) and 10080 (168h = one full week).
  weeklyMinuteTarget: z.number().int().min(60).max(10080).nullable(),
});
export type PatchAdminUserHourTargetRequest = z.infer<typeof PatchAdminUserHourTargetRequest>;

export const PatchAdminUserSystemStartDateRequest = z.object({
  // The date this person landed in the system, as `YYYY-MM-DD`. null clears the
  // override and restores the `created_at`-derived fallback.
  //
  // The future-date rule is enforced server-side rather than here: "future"
  // depends on the America/Chicago calendar day, and this schema is also
  // evaluated in the browser, where the clock is the viewer's.
  systemStartDate: isoCalendarDate.nullable(),
});
export type PatchAdminUserSystemStartDateRequest = z.infer<
  typeof PatchAdminUserSystemStartDateRequest
>;

export const AdminUserMembershipDto = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  color: z.string(),
  role: z.enum(['contributor', 'approver']),
  isLead: z.boolean(),
});
export type AdminUserMembershipDto = z.infer<typeof AdminUserMembershipDto>;

export const AdminUserMembershipsResponse = z.array(AdminUserMembershipDto);
export type AdminUserMembershipsResponse = z.infer<typeof AdminUserMembershipsResponse>;

export const AdminUserLineSourceTeamDto = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  color: z.string(),
});

export const AdminUserLineDto = z.object({
  assignmentId: z.string().uuid(),
  activeFlag: z.boolean(),
  projectId: z.string().uuid(),
  projectName: z.string(),
  projectCode: z.string(),
  taskId: z.string().uuid(),
  taskName: z.string(),
  source: z.enum(['direct', 'team_fan_out']),
  sourceTeam: AdminUserLineSourceTeamDto.nullable(),
  hoursYtd: z.number(),
});
export type AdminUserLineDto = z.infer<typeof AdminUserLineDto>;

export const AdminUserLinesResponse = z.array(AdminUserLineDto);
export type AdminUserLinesResponse = z.infer<typeof AdminUserLinesResponse>;

export const AdSyncResultDto = z.object({
  /**
   * Identifies the run that produced this result, so a caller that started a run
   * can tell its own outcome from somebody else's. Absent on scheduled runs,
   * which nobody is waiting on, and on results recorded before run IDs existed.
   */
  runId: z.string().nullish(),
  ranAt: z.string(),
  total: z.number().int(),
  added: z.number().int(),
  updated: z.number().int(),
  deactivated: z.number().int(),
  /**
   * People missing from the group but still inside the deactivation grace
   * period. Optional: absent on runs that applied nothing, and on results stored
   * before the grace period existed — which must keep parsing, or the admin
   * panel's "Last sync" goes blank.
   */
  pendingDeactivation: z.number().int().optional(),
  durationMs: z.number().int(),
  errors: z.array(z.string()),
});
export type AdSyncResultDto = z.infer<typeof AdSyncResultDto>;

/**
 * "Sync now" starts the run and returns immediately, because a sufficiently
 * large roster takes longer to reconcile than API Gateway's 29-second
 * integration ceiling allows a request to live.
 *
 * A caller polls `GET /v1/admin/ad-sync/config` and treats the run as finished
 * when **either** signal carries this `runId`:
 *
 * - `lastSync.runId` — the result of the most recent run, or
 * - `recentRunIds` — the ids of recently completed runs.
 *
 * Both are needed. Matching on the id rather than on "has `lastSync` changed"
 * is what stops a caller reporting completion off somebody else's run: runs
 * overlap by design, serializing on the roster lock rather than excluding each
 * other. But `lastSync` is a single slot, so a run that finishes just after this
 * one can replace it before the next poll — watching only `lastSync` would then
 * miss this run entirely and time out. `recentRunIds` is what makes completion
 * observable rather than something that has to be caught in the act.
 *
 * Note that when completion is detected only via `recentRunIds`, `lastSync`
 * belongs to a different run, so its counts do not describe this one.
 */
export const AdSyncStartedDto = z.object({
  started: z.literal(true),
  runId: z.string(),
});
export type AdSyncStartedDto = z.infer<typeof AdSyncStartedDto>;

export const AdSyncConfigDto = z.object({
  enabled: z.boolean(),
  hourCt: z.number().int().min(0).max(23),
  groupDn: z.string(),
  lastSync: AdSyncResultDto.nullable(),
  /**
   * Ids of recently completed runs, newest first. `lastSync` is a single slot
   * that an overlapping run can overwrite within one poll interval, so a caller
   * checks here for its own run rather than having to catch it in that slot.
   */
  recentRunIds: z.array(z.string()).default([]),
});
export type AdSyncConfigDto = z.infer<typeof AdSyncConfigDto>;

export const UpdateAdSyncConfigRequest = z.object({
  enabled: z.boolean(),
  hourCt: z.number().int().min(0).max(23),
  groupDn: z.string(),
});
export type UpdateAdSyncConfigRequest = z.infer<typeof UpdateAdSyncConfigRequest>;
